/**
 * Der Betrachter fuer die abgelegte PDF.
 *
 * Der eingebaute Betrachter des Browsers bringt eine dunkle Leiste, eine
 * Seitenvorschau und einen fast schwarzen Grund mit. Auf einer halben
 * Bildschirmbreite frisst das Platz und steht neben einer hellen Oberflaeche
 * wie ein Fremdkoerper. Deshalb zeichnen wir die Seiten selbst.
 *
 * Muster uebernommen aus dem Kontoauszugwerkzeug des alten Prototyps —
 * fortlaufende Seiten, heller Grund, Zoom mit Strg und Mausrad am Zeiger,
 * Doppelklick zurueck auf hundert Prozent.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import * as pdfjs from 'pdfjs-dist'
import arbeiterQuelle from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

pdfjs.GlobalWorkerOptions.workerSrc = arbeiterQuelle

/* Feiner als die Bildschirmaufloesung, damit die Seite beim Hineinzoomen nicht
   sofort weich wird. */
const AUFLOESUNG = 2

/* Wie weit ueber den Rand hinaus vorgezeichnet wird. Eine Bildschirmhoehe nach
   oben und unten reicht, damit beim Blaettern nichts leer aufblitzt. */
const VORLAUF = '100% 0px'

/* So viele Seiten werden immer gezeichnet und nie wieder freigegeben. Zum
   Anfang eines Berichts kehrt man staendig zurueck. */
const ERSTE_SEITEN = 3

/* Bis auf ein Drittel heraus: dann liegen drei Seiten nebeneinander im Blick
   und man findet die Stelle, die man sucht. Vier Mal heran reicht, um eine
   Fussnote zu lesen. */
const ZOOM_MIN = 0.33
const ZOOM_MAX = 4
const ZOOM_GANZ = 1
const ZOOM_NAH = 1.8

/* Der Zoom waechst nicht in festen Schritten, sondern verhaeltnismaessig: von
   0,5 auf 0,55 ist gefuehlt derselbe Sprung wie von 2 auf 2,2.
 *
 * Zwei Tempi, weil zwei sehr verschiedene Eingaben denselben Weg nehmen. Ein
 * Mausrad meldet sich einmal je Raste mit einem Wert um 100. Ein Trackpad
 * meldet sich beim Aufziehen dutzendfach mit Werten um 2. Mit einem einzigen
 * Faktor geht eines von beidem nicht: entweder springt das Rad durch den
 * halben Bereich, oder das Aufziehen bewegt gar nichts. */
const ZOOM_TEMPO_RAD = 0.0007
const ZOOM_TEMPO_FINGER = 0.010
/* Ab hier ist es ein Mausrad. Trackpads bleiben deutlich darunter, Raeder
   liegen deutlich darueber. */
const RAD_AB = 25

/* Hundert Prozent ist ein Halt, kein Band.
 *
 * Eine Bewegung, die hundert Prozent kreuzt oder dicht daran vorbeikommt,
 * bleibt dort stehen und ruht bis zum Ende dieser Bewegung. Wer weiter will,
 * setzt neu an. Das ist dasselbe Verhalten wie beim Einrasten eines Fensters
 * am Bildschirmrand, und es trifft hundert Prozent immer — im Gegensatz zu
 * einem Band, das je nach Tempo mal zu breit und mal zu schmal ist. */

/* Kleines Fangfenster fuer den Fall, dass eine Bewegung dicht vor hundert
   Prozent endet, statt sie zu kreuzen. */
const RASTE_BAND = 0.03

/* Pause, ab der eine neue Bewegung beginnt. Ein Trackpad meldet sich beim
   Aufziehen alle paar Millisekunden, einzelne Rasten am Mausrad liegen weiter
   auseinander. */
const GESTE_PAUSE_MS = 120

/**
 * Das Aufziehen mit zwei Fingern meldet sich je nach Browser verschieden.
 *
 * Chrome und Firefox schicken ein Radereignis mit gedrueckter Strg-Taste —
 * dieselbe Geste, die eine Maus mit Strg und Rad ausloest, nur mit viel
 * kleineren Werten. Safari schickt stattdessen eigene Ereignisse,
 * `gesturestart`, `gesturechange` und `gestureend`, mit einem `scale` relativ
 * zum Beginn der Geste. Sie stehen in keinem Standard und fehlen deshalb in
 * den Typen des Browsers.
 */
interface Gestenereignis extends Event {
  readonly scale: number
  readonly clientX: number
  readonly clientY: number
}

const begrenzen = (wert: number): number => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, wert))

export function Blattbetrachter({
  quelle,
  titel,
  sprung,
}: {
  quelle: string
  titel: string
  /** Zu dieser Seite blaettern. `mal` zaehlt hoch, damit auch derselbe Sprung zweimal wirkt. */
  sprung?: { seite: number; mal: number } | null
}) {
  const flaeche = useRef<HTMLDivElement>(null)
  const zoom = useRef(ZOOM_GANZ)
  /* Wann die letzte Zoombewegung gemeldet hat und ob sie bei hundert Prozent
     zum Halten gekommen ist. Beides zusammen macht aus einer Folge von
     Einzelereignissen eine Bewegung. */
  const letzteBewegung = useRef(0)
  const haelt = useRef(false)
  const [anzeigeZoom, setAnzeigeZoom] = useState(ZOOM_GANZ)
  const [seiten, setSeiten] = useState<number | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  /**
   * Zoomen und dabei den Punkt unter dem Zeiger festhalten.
   *
   * Nicht ueber einen Streckfaktor gerechnet, sondern an der Seite selbst
   * gemessen: die Abstaende zwischen den Seiten und der Rand ringsum wachsen
   * beim Zoomen **nicht** mit. Wer die Scrollposition einfach mal dem Faktor
   * nimmt, laeuft mit jeder Seite weiter nach unten weg — bei 46 Seiten sind
   * das 720 Pixel, die nie skalieren.
   */
  const zoomen = useCallback((zielRoh: number, anker?: { x: number; y: number }) => {
    const v = flaeche.current
    if (!v) return
    const neu = begrenzen(zielRoh)
    if (neu === zoom.current) return

    const kasten = v.getBoundingClientRect()
    const mx = anker ? anker.x - kasten.left : v.clientWidth / 2
    const my = anker ? anker.y - kasten.top : v.clientHeight / 2
    const zielX = v.scrollLeft + mx
    const zielY = v.scrollTop + my

    /* Die Seite unter dem Zeiger. Steht er in einer Luecke zwischen zwei
       Seiten, nimmt die Suche die naechstgelegene — dann verrutscht es um
       wenige Pixel statt um eine Seitenhoehe. */
    const blaetter = Array.from(v.children) as HTMLElement[]
    let seite: HTMLElement | null = null
    let abstand = Infinity
    for (const b of blaetter) {
      const oben = b.offsetTop
      const unten = oben + b.offsetHeight
      const d = zielY < oben ? oben - zielY : zielY > unten ? zielY - unten : 0
      if (d < abstand) {
        abstand = d
        seite = b
      }
      if (d === 0) break
    }

    const relX = seite ? (zielX - seite.offsetLeft) / seite.offsetWidth : 0
    const relY = seite ? (zielY - seite.offsetTop) / seite.offsetHeight : 0

    zoom.current = neu
    setAnzeigeZoom(neu)
    v.style.setProperty('--zoom', String(neu))

    if (!seite) return
    // Lesen erzwingt den Umbruch, danach stehen die neuen Masse fest.
    void v.offsetHeight
    v.scrollLeft = seite.offsetLeft + relX * seite.offsetWidth - mx
    v.scrollTop = seite.offsetTop + relY * seite.offsetHeight - my
  }, [])

  /**
   * Eine Zoombewegung auswerten.
   *
   * `faktor` ist der Schritt gegenueber dem gerade angezeigten Zoom. Kreuzt
   * der Schritt hundert Prozent oder endet er dicht davor, bleibt der
   * Betrachter dort stehen und ruehrt sich bis zum Ende dieser Bewegung nicht
   * mehr. Weiter geht es erst, wenn neu angesetzt wird.
   */
  const bewegen = useCallback(
    (faktor: number, zeit: number, anker: { x: number; y: number }, neueBewegung = false) => {
      if (neueBewegung || zeit - letzteBewegung.current > GESTE_PAUSE_MS) haelt.current = false
      letzteBewegung.current = zeit
      if (haelt.current) return

      const jetzt = zoom.current
      const ziel = begrenzen(jetzt * faktor)

      const kreuzt = (jetzt - ZOOM_GANZ) * (ziel - ZOOM_GANZ) < 0
      /* Naehert sich die Bewegung hundert Prozent und endet dicht davor, wird
         sie ebenfalls dort angehalten. Ohne die Richtungspruefung wuerde eine
         Bewegung, die bei genau hundert Prozent beginnt, sofort wieder
         festgehalten und man kaeme nie weg. */
      const naehert = Math.abs(ziel - ZOOM_GANZ) < Math.abs(jetzt - ZOOM_GANZ)
      const dichtDran = naehert && Math.abs(ziel - ZOOM_GANZ) < RASTE_BAND

      if (kreuzt || dichtDran) {
        haelt.current = true
        zoomen(ZOOM_GANZ, anker)
        return
      }
      zoomen(ziel, anker)
    },
    [zoomen],
  )

  useEffect(() => {
    const v = flaeche.current
    if (!v) return

    const rad = (e: WheelEvent) => {
      // Ohne Strg beziehungsweise Cmd bleibt es beim Blaettern. Das Aufziehen
      // mit zwei Fingern kommt in Chrome und Firefox genau so an: als
      // Radereignis mit gesetztem ctrlKey, nur mit viel kleineren Werten.
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const tempo = Math.abs(e.deltaY) >= RAD_AB ? ZOOM_TEMPO_RAD : ZOOM_TEMPO_FINGER
      bewegen(Math.exp(-e.deltaY * tempo), e.timeStamp, { x: e.clientX, y: e.clientY })
    }

    const doppelklick = (e: MouseEvent) => {
      // Der Doppelklick setzt unmittelbar, ohne Halt und ohne Bewegung.
      haelt.current = false
      letzteBewegung.current = 0
      zoomen(zoom.current === ZOOM_GANZ ? ZOOM_NAH : ZOOM_GANZ, { x: e.clientX, y: e.clientY })
    }

    /* Safari: das Aufziehen mit zwei Fingern kommt als eigene Geste, nicht als
       Radereignis. `scale` zaehlt vom Beginn der Geste, deshalb wird hier der
       Schritt gegenueber der letzten Meldung gebildet. */
    let letzteSkala = 1
    const gesteBeginnt = (e: Event) => {
      e.preventDefault()
      letzteSkala = 1
      haelt.current = false
      letzteBewegung.current = e.timeStamp
    }
    const gesteLaeuft = (e: Event) => {
      e.preventDefault()
      const g = e as Gestenereignis
      const schritt = g.scale / letzteSkala
      letzteSkala = g.scale
      bewegen(schritt, g.timeStamp, { x: g.clientX, y: g.clientY })
    }
    const gesteEndet = (e: Event) => e.preventDefault()

    // passive:false, sonst laesst der Browser das Standardverhalten nicht
    // abbestellen und die ganze Seite zoomt mit.
    v.addEventListener('wheel', rad, { passive: false })
    v.addEventListener('dblclick', doppelklick)
    v.addEventListener('gesturestart', gesteBeginnt, { passive: false })
    v.addEventListener('gesturechange', gesteLaeuft, { passive: false })
    v.addEventListener('gestureend', gesteEndet, { passive: false })
    return () => {
      v.removeEventListener('wheel', rad)
      v.removeEventListener('dblclick', doppelklick)
      v.removeEventListener('gesturestart', gesteBeginnt)
      v.removeEventListener('gesturechange', gesteLaeuft)
      v.removeEventListener('gestureend', gesteEndet)
    }
  }, [bewegen, zoomen])

  useEffect(() => {
    const v = flaeche.current
    if (!v) return

    /* Wird waehrend des Zeichnens ein anderes Dokument geoeffnet, muessen die
       laufenden Seiten verworfen werden — sonst haengen sie sich unter das
       neue. */
    let verworfen = false
    setLaedt(true)
    setFehler(null)
    setSeiten(null)
    v.replaceChildren()
    zoom.current = ZOOM_GANZ
    haelt.current = false
    letzteBewegung.current = 0
    setAnzeigeZoom(ZOOM_GANZ)
    v.style.setProperty('--zoom', '1')

    const auftrag = pdfjs.getDocument({ url: quelle, withCredentials: true })
    /* Nur zeichnen, was in der Naehe ist. Alle 46 Seiten eines Bilanzberichts
       gleichzeitig in Bildpunkten vorzuhalten waeren rund 370 MB — und der
       Betrachter bliebe sekundenlang leer, bevor die erste Seite steht. */
    let waechter: IntersectionObserver | null = null

    void (async () => {
      try {
        const pdf = await auftrag.promise
        if (verworfen) return
        setLaedt(false)

        const aufgaben = new Map<Element, number>()

        /** Eine Seite in ihren Platz zeichnen. Doppelte Auftraege prallen ab. */
        const zeichnen = async (platz: HTMLElement, nr: number): Promise<void> => {
          if (platz.dataset['gezeichnet']) return
          platz.dataset['gezeichnet'] = 'ja'
          try {
            const seite = await pdf.getPage(nr)
            if (verworfen || !platz.dataset['gezeichnet']) return
            const sicht = seite.getViewport({ scale: AUFLOESUNG })
            const blatt = document.createElement('canvas')
            blatt.width = sicht.width
            blatt.height = sicht.height
            blatt.setAttribute('aria-label', `Seite ${nr} von ${pdf.numPages}`)
            /* In pdf.js 5 schliessen `canvas` und `canvasContext` einander aus.
               Wer beides uebergibt, bekommt keinen Fehler, sondern ein
               Versprechen, das nie einloest. */
            await seite.render({ canvas: blatt, viewport: sicht }).promise
            if (verworfen || !platz.dataset['gezeichnet']) return
            platz.replaceChildren(blatt)
          } catch {
            platz.dataset['gezeichnet'] = ''
          }
        }

        waechter = new IntersectionObserver(
          (eintraege) => {
            for (const eintrag of eintraege) {
              const nr = aufgaben.get(eintrag.target)
              if (nr === undefined) continue
              const platz = eintrag.target as HTMLElement
              if (eintrag.isIntersecting) {
                void zeichnen(platz, nr)
              } else if (nr > ERSTE_SEITEN) {
                // Weit weg: Bildpunkte freigeben, der Platzhalter bleibt.
                // Die ersten Seiten behalten wir, dorthin kehrt man staendig
                // zurueck.
                platz.replaceChildren()
                platz.dataset['gezeichnet'] = ''
              }
            }
          },
          { root: v, rootMargin: VORLAUF },
        )

        /* Erst alle Plaetze anlegen, dann zeichnen lassen. So stimmen Hoehe und
           Scrollweg vom ersten Augenblick an, und die Bildlaufleiste springt
           nicht, waehrend man schon liest. */
        const plaetze: HTMLElement[] = []
        for (let nr = 1; nr <= pdf.numPages; nr += 1) {
          const seite = await pdf.getPage(nr)
          if (verworfen) return
          const sicht = seite.getViewport({ scale: 1 })
          const platz = document.createElement('div')
          platz.className = 'blatt-seite'
          platz.style.aspectRatio = `${sicht.width} / ${sicht.height}`
          v.appendChild(platz)
          plaetze.push(platz)
          aufgaben.set(platz, nr)
          waechter.observe(platz)
        }
        /* Erst jetzt ist die Seitenzahl bekannt gegeben: der Sprung zur ersten
           Seite eines Auszugs braucht die Plaetze im DOM, sonst springt er ins
           Leere. Vorher gab es die Zahl, aber keine Ziele. */
        setSeiten(pdf.numPages)

        /* Die ersten Seiten ohne Umweg ueber den Beobachter. Der meldet sich
           erst, wenn der Reiter im Vordergrund steht — wer ein Dokument in
           einem Hintergrundreiter oeffnet, saehe sonst beim Hinschauen einen
           Moment lang nur leere Blaetter. */
        for (const [i, platz] of plaetze.slice(0, ERSTE_SEITEN).entries()) {
          void zeichnen(platz, i + 1)
        }
      } catch (e) {
        if (verworfen) return
        setLaedt(false)
        setFehler(e instanceof Error ? e.message : 'Die Datei ist nicht lesbar.')
      }
    })()

    return () => {
      verworfen = true
      waechter?.disconnect()
      void auftrag.destroy()
    }
  }, [quelle])

  /* Ein Klick auf eine Buchung blaettert zu ihrer Seite. Wo die Buchung auf
     der Seite steht, weiss nur die KI, und die zeigt es nicht belegbar an —
     deshalb die Seite, nicht eine Markierung an einer geratenen Stelle. */
  useEffect(() => {
    const v = flaeche.current
    if (!v || !sprung || seiten === null) return
    const ziel = v.children[sprung.seite - 1] as HTMLElement | undefined
    if (ziel) v.scrollTo({ top: ziel.offsetTop - 12, behavior: 'smooth' })
  }, [sprung, seiten])

  return (
    <div className="blattbetrachter">
      <div className="blatt-flaeche" ref={flaeche} role="document" aria-label={titel} />
      <p className="blatt-fuss">
        {laedt && 'Wird geladen …'}
        {fehler && <span className="warnung">{fehler}</span>}
        {!laedt && !fehler && seiten !== null && (
          <>
            {seiten} {seiten === 1 ? 'Seite' : 'Seiten'} · {Math.round(anzeigeZoom * 100)} % · Zwei Finger oder
            Strg und Mausrad zoomt, Doppelklick zurück auf 100 %
          </>
        )}
      </p>
    </div>
  )
}
